import { Status } from "components";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import PhoneRow from "components/DetailPage/PhoneRow";
import { MergeFormSection } from "components/MainSectionTemplate/types";
import { convertToPhoneNumbers } from "components/PhoneInput";
import TagList from "components/Tag/TagList";
import ContactEmailRow from "contact/detail/components/ContactEmailRow";
import { ContactFormState } from "contact/models";
import React from "react";
import { isNullOrEmptyString } from "shared/utils";
import { getGenderName } from "utilities/genderUtils";

export const mainSection = (
  existingContact: ContactFormState,
  sourceContact: ContactFormState,
  currentContact: ContactFormState,
  setCurrentContact: (contact: ContactFormState) => void,
  existingTags: server.dto.TagSmall[],
  sourceTags: server.dto.TagSmall[],
  currentTags: server.dto.TagSmall[],
  setCurrentTags: (tags: server.dto.TagSmall[]) => void,
): MergeFormSection => ({
  key: "information",
  title: RESX.GeneralLabels.Information,
  fields: [{
    key: "name",
    label: RESX.GeneralLabels.Name,
    currentValue: [currentContact.firstName, currentContact.insertion, currentContact.lastName].filter(Boolean).join(" "),
    existingValue: [existingContact.firstName, existingContact.insertion, existingContact.lastName].filter(Boolean).join(" "),
    sourceValue: [sourceContact.firstName, sourceContact.insertion, sourceContact.lastName].filter(Boolean).join(" "),
    isEmpty: () => false,
    render: (value) => <span>{value}</span>,
    onSourceClick: () => setCurrentContact({
      ...currentContact,
      firstName: sourceContact.firstName,
      insertion: sourceContact.insertion,
      lastName: sourceContact.lastName
    }),
    onExistingClick: () => setCurrentContact({
      ...currentContact,
      firstName: existingContact.firstName,
      insertion: existingContact.insertion,
      lastName: existingContact.lastName
    }),
  }, {
    key: "emailAddress",
    label: RESX.GeneralLabels.Email,
    currentValue: {
      emailAddresses: currentContact.emailAddresses,
      isEmailBlacklisted: currentContact.isEmailBlacklisted,
    },
    existingValue: {
      emailAddresses: existingContact.emailAddresses,
      isEmailBlacklisted: existingContact.isEmailBlacklisted,
    },
    sourceValue: {
      emailAddresses: sourceContact.emailAddresses,
      isEmailBlacklisted: sourceContact.isEmailBlacklisted,
    },
    isEmpty: (value) => isNullOrEmptyString(value.emailAddresses),
    render: (value) => <ContactEmailRow emailAddresses={value.emailAddresses} emailBlacklisted={value.isEmailBlacklisted} />,
    onSourceClick: () => setCurrentContact({
      ...currentContact,
      emailAddresses: sourceContact.emailAddresses,
      isEmailBlacklisted: sourceContact.isEmailBlacklisted,
    }),
    onExistingClick: () => setCurrentContact({
      ...currentContact,
      emailAddresses: existingContact.emailAddresses,
      isEmailBlacklisted: existingContact.isEmailBlacklisted
    }),
  }, {
    key: "mobileNumbers",
    label: RESX.Contact.Mobile,
    currentValue: currentContact.mobileNumbers,
    existingValue: existingContact.mobileNumbers,
    sourceValue: sourceContact.mobileNumbers,
    isEmpty: (value) => isNullOrEmptyString(value),
    render: (value) => <PhoneRow numbers={convertToPhoneNumbers(value)} />,
    onSourceClick: () => setCurrentContact({ ...currentContact, mobileNumbers: sourceContact.mobileNumbers }),
    onExistingClick: () => setCurrentContact({ ...currentContact, mobileNumbers: existingContact.mobileNumbers }),
  }, {
    key: "landline",
    label: RESX.ContactDetails.PhoneNumber,
    currentValue: currentContact.landlineNumbers,
    existingValue: existingContact.landlineNumbers,
    sourceValue: sourceContact.landlineNumbers,
    isEmpty: (value) => isNullOrEmptyString(value),
    render: (value) => <PhoneRow numbers={convertToPhoneNumbers(value)} />,
    onSourceClick: () => setCurrentContact({ ...currentContact, landlineNumbers: sourceContact.landlineNumbers }),
    onExistingClick: () => setCurrentContact({ ...currentContact, landlineNumbers: existingContact.landlineNumbers }),
  }, {
    key: "language",
    label: RESX.Language.resxLanguage,
    currentValue: currentContact.culture,
    existingValue: existingContact.culture,
    sourceValue: sourceContact.culture,
    isEmpty: () => false,
    render: (v: server.dto.LCID) => serverReference.templateCultures.find(c => c.lcid === v).text,
    onSourceClick: () => setCurrentContact({ ...currentContact, culture: sourceContact.culture }),
    onExistingClick: () => setCurrentContact({ ...currentContact, culture: existingContact.culture }),
  }, {
    key: "gender",
    label: RESX.Contact.Gender,
    currentValue: currentContact.gender,
    existingValue: existingContact.gender,
    sourceValue: sourceContact.gender,
    isEmpty: (value: server.dto.Genders) => value === "unspecified",
    render: (value: server.dto.Genders) => getGenderName(value),
    onSourceClick: () => setCurrentContact({ ...currentContact, gender: sourceContact.gender }),
    onExistingClick: () => setCurrentContact({ ...currentContact, gender: existingContact.gender }),
  }, {
    key: "position",
    label: RESX.Contact.Position,
    currentValue: currentContact.position,
    existingValue: existingContact.position,
    sourceValue: sourceContact.position,
    isEmpty: (value) => isNullOrEmptyString(value),
    render: (value) => <span>{value}</span>,
    onSourceClick: () => setCurrentContact({ ...currentContact, position: sourceContact.position }),
    onExistingClick: () => setCurrentContact({ ...currentContact, position: existingContact.position }),
  }, {
    key: "department",
    label: RESX.Contact.Department,
    currentValue: currentContact.department,
    existingValue: existingContact.department,
    sourceValue: sourceContact.department,
    isEmpty: (value) => !value,
    render: (value) => <span>{value}</span>,
    onSourceClick: () => setCurrentContact({ ...currentContact, department: sourceContact.department }),
    onExistingClick: () => setCurrentContact({ ...currentContact, department: existingContact.department }),
  }, {
    key: "tags",
    label: RESX.Tag.Tags,
    currentValue: currentTags,
    existingValue: existingTags,
    sourceValue: sourceTags,
    isEmpty: (v) => v.length === 0,
    render: (value: server.dto.TagSmall[]) => <TagList tags={value} length="short" />,
    onSourceClick: () => setCurrentTags(sourceTags),
    onExistingClick: () => setCurrentTags(existingTags),
  }, {
    key: "salutation",
    label: RESX.Contact.Salutation,
    currentValue: currentContact.salutation,
    existingValue: existingContact.salutation,
    sourceValue: sourceContact.salutation,
    isEmpty: (value) => isNullOrEmptyString(value),
    render: (value) => <span>{value}</span>,
    onSourceClick: () => setCurrentContact({ ...currentContact, salutation: sourceContact.salutation }),
    onExistingClick: () => setCurrentContact({ ...currentContact, salutation: existingContact.salutation }),
  }, {
    key: "status",
    label: RESX.Status.resxStatus,
    currentValue: currentContact.status,
    existingValue: existingContact.status,
    sourceValue: sourceContact.status,
    isEmpty: () => false,
    render: (value) => <Status status={value} />,
    onSourceClick: () => setCurrentContact({ ...currentContact, status: sourceContact.status }),
    onExistingClick: () => setCurrentContact({ ...currentContact, status: existingContact.status }),
  }, {
    key: "owner",
    label: RESX.GeneralLabels.Owner,
    currentValue: currentContact.owner,
    existingValue: existingContact.owner,
    sourceValue: sourceContact.owner,
    isEmpty: () => false,
    render: (v) => <UserWithAvatar user={v} />,
    onSourceClick: () => setCurrentContact({ ...currentContact, owner: sourceContact.owner }),
    onExistingClick: () => setCurrentContact({ ...currentContact, owner: existingContact.owner }),
  }]
})